import React, { useMemo, useState } from "react";
import "./AIImagePopup.css";
import { getFunctions, httpsCallable } from "firebase/functions";

const STYLE_OPTIONS = [
  { value: "realistic", label: "Food photography realistica" },
  { value: "gourmet", label: "Gourmet / Fine dining" },
  { value: "flat", label: "Flat / Minimal" },
  { value: "social", label: "Social / Instagram" },
  { value: "brand", label: "Coerente con il brand del ristorante" }
];

const QUICK_PRESETS = ["più luminoso", "più gourmet", "più social"];

export default function AIImagePopup({
  visible,
  onClose,
  onComplete,
  dishName,
  aiCredits,
  localeTag
}) {
  const [styles, setStyles] = useState([]);
  const [loading, setLoading] = useState(false);

  const [generatedImages, setGeneratedImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  const [previousImage, setPreviousImage] = useState(null);
  const [editPrompt, setEditPrompt] = useState("");

  const [restaurantStyle] = useState(localStorage.getItem("ai_style") || "");

  const functions = useMemo(() => getFunctions(), []);
  const generateDishImage = useMemo(
    () => httpsCallable(functions, "generateDishImage"),
    [functions]
  );

  if (!visible) return null;

  const resetState = () => {
    setStyles([]);
    setLoading(false);
    setGeneratedImages([]);
    setSelectedImage(null);
    setPreviewImage(null);
    setPreviousImage(null);
    setEditPrompt("");
  };

  const handleClose = () => {
    if (loading) return;
    resetState();
    onClose();
  };

  const toggleStyle = (value) => {
    if (loading) return;

    if (styles.includes(value)) {
      // evito di rimanere con 0 stili selezionati
      if (styles.length === 1) return;
      setStyles((prev) => prev.filter((v) => v !== value));
      return;
    }

    if (styles.length < 2) {
      setStyles((prev) => [...prev, value]);
    }
  };

  const callGenerate = async (promptText) => {
    const result = await generateDishImage({
      prompt: promptText,
      localeTag: localeTag || localStorage.getItem("localeTag")
    });

    const base64Image = result?.data?.base64Image;
    if (!base64Image) {
      throw new Error("Nessuna immagine generata");
    }

    return base64Image;
  };

  const buildGeneratePrompt = (styleValue, variantText = "") => {
    const variantLine = variantText ? `Variation: ${variantText}\n` : "";

    return `
Professional food photography of an Italian dish.

Dish name: ${dishName}
Style: ${styleValue}
Restaurant style: ${restaurantStyle}
${variantLine}
High quality, realistic lighting, appetizing, clean background,
restaurant menu photography, no text, no watermark.
`.trim();
  };

  const handleGenerateImage = async () => {
    if (loading || styles.length === 0 || !aiCredits) return;

    try {
      setLoading(true);

      // reset completo del flusso precedente
      setGeneratedImages([]);
      setSelectedImage(null);
      setPreviewImage(null);
      setPreviousImage(null);
      setEditPrompt("");

      localStorage.setItem("ai_style", styles.join(","));

      const results = [];

      // Caso A: 2 stili selezionati -> 2 immagini, una per stile
      if (styles.length === 2) {
        for (const styleValue of styles) {
          const prompt = buildGeneratePrompt(styleValue);
          const image = await callGenerate(prompt);
          results.push({
            id: `${styleValue}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
            image,
            style: styleValue
          });
        }
      }

      // Caso B: 1 solo stile -> 2 varianti dello stesso stile
      // Caso B: 1 solo stile -> 1 immagine
      if (styles.length === 1) {
        const styleValue = styles[0];

        const prompt = buildGeneratePrompt(styleValue);

        const image = await callGenerate(prompt);

        results.push({
          id: `${styleValue}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          image,
          style: styleValue
        });
      }

      setGeneratedImages(results);
    } catch (err) {
      console.error("Errore generazione immagini:", err);
      alert(
        err?.message ||
          err?.details ||
          "Errore durante la generazione delle immagini"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSelectGeneratedImage = (img) => {
    if (loading) return;
    setSelectedImage(img);
    setPreviewImage(img); // 🔥 QUESTO attiva la preview
    setPreviousImage(null);
  };

  const handleUse = () => {
    const imgToUse = previewImage || selectedImage;

    if (!imgToUse) return;

    const base64 = imgToUse.includes(",")
      ? imgToUse.split(",")[1]
      : imgToUse;

    onComplete({
      imageBase64: base64
    });

    handleClose();
  };

  const handleEditImage = async () => {
    if (!editPrompt.trim() || loading || !(previewImage || selectedImage)) return;

    try {
      setLoading(true);

      // salvo il "prima"
      const baseImage = previewImage || selectedImage;
      setPreviousImage(baseImage);

      // nascondo il "dopo" finché non arriva davvero
      setPreviewImage(null);

      localStorage.setItem("ai_style", styles.join(","));

      const styleForEdit = styles[0] || "realistic";

      const prompt = `
Modify the existing food image concept.

Original dish: ${dishName}
Current style: ${styleForEdit}
Restaurant style: ${restaurantStyle}

User request:
${editPrompt}

Keep realistic food photography, natural lighting,
restaurant quality, appetizing presentation, no text, no watermark.
Generate a revised version based on the requested changes.
`.trim();

      const editedImage = await callGenerate(prompt);

      setPreviewImage(editedImage);
      setSelectedImage(editedImage);
      setEditPrompt("");
    } catch (err) {
      console.error("Errore modifica immagine:", err);
      alert(
        err?.message ||
          err?.details ||
          "Errore durante la modifica dell'immagine"
      );
    } finally {
      setLoading(false);
    }
  };

  const showGeneratedChoices =
    generatedImages.length > 0;

  const showSinglePreview =
    selectedImage && !previousImage && !loading;

  const showCompare =
    previousImage && previewImage && !loading;

  const showLoadingCompare =
    loading && previousImage && !previewImage;

  return (
    <div className="ai-overlay">
      <div className="ai-popup">
        <div className="ai-header">

  <div className="ai-sheet-handle" />

  <h2 className="ai-title">
    Genera immagine
    <span>{dishName}</span>
  </h2>



</div>

        <div className="ai-content">
          
<p className="ai-style-notice">
  Scegli fino a 2 stili
</p>

          <div className="ai-style-grid">
            {STYLE_OPTIONS.map((s) => (
              <button
                key={s.value}
                type="button"
                disabled={loading}
                className={`ai-style-btn ${styles.includes(s.value) ? "selected" : ""}`}
                onClick={() => toggleStyle(s.value)}
              >
                {s.label}
              </button>
            ))}
          </div>

      

          <button
            type="button"
            className="ai-btn-primary"
            disabled={loading || !aiCredits || styles.length === 0}
            onClick={handleGenerateImage}
          >
            {loading
              ? "⏳ Creazione immagini..."
              : `✨ Genera ${styles.length} ${styles.length === 1 ? "immagine" : "immagini"}`}
          </button>

          <p className="ai-credit-hint">
  Crediti disponibili: <strong>{aiCredits}</strong>
</p>

          {loading && !previousImage && (
            <p className="ai-loading">⏳ Stiamo creando le immagini...</p>
          )}

          <div className="ai-image-list">
            {showGeneratedChoices && generatedImages.length > 0 && (
              <div className="ai-multi-select">
                {generatedImages.map((item, i) => (
                  <div
                    key={item.id}
                   onClick={() => {
  handleSelectGeneratedImage(item.image);
}}
                    style={{
                      borderRadius: "12px",
                      overflow: "hidden",
                      cursor: "pointer",
                      border: selectedImage === item.image
                        ? "3px solid #2ecc71"
                        : "2px solid transparent",
                      transform: selectedImage === item.image ? "scale(1.05)" : "scale(1)",
                      boxShadow: selectedImage === item.image
                        ? "0 10px 25px rgba(0,0,0,0.4)"
                        : "none",
                      transition: "0.2s"
                    }}
                  >
                    <div className="ai-image-wrapper">
                      <img
                        src={item.image}
                        alt={`Opzione ${i + 1}`}
                        style={{
                          width: "100%",
                          display: "block"
                        }}
                      />
                      {selectedImage === item.image && (
                        <div className="ai-selected-overlay">
                          <div className="ai-check">✓</div>
                        </div>
                      )}
                    </div>
                    <span className="ai-select-label">
                      Opzione {i + 1} · {item.style}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {showLoadingCompare && (
              <div className="ai-loading-compare">
                <p className="ai-loading-compare-title">⏳ Modifica in corso...</p>
                <div className="ai-loading-compare-box">
                  <p>Prima</p>
                  <div
                    onClick={() => setSelectedImage(previousImage)}
                    style={{
                      border: selectedImage === previousImage
                        ? "3px solid #2ecc71"
                        : "2px solid transparent"
                    }}
                  >
                    <img src={previousImage} alt="Prima" />
                  </div>
                </div>
              </div>
            )}

            {showCompare && (

              <div className="ai-preview">

  <p className="ai-sub">
    Confronta le versioni
  </p>
                <div className="ai-compare">
                  <div
                    onClick={() => setSelectedImage(previousImage)}
                    style={{
                      cursor: "pointer",
                      border: selectedImage === previousImage
                        ? "3px solid #2ecc71"
                        : "2px solid transparent",
                      borderRadius: "12px",
                      padding: "4px"
                    }}
                  >
                    <p>Prima</p>
                    <img src={previousImage} alt="Prima" />
                  </div>

                  <div
                    onClick={() => setSelectedImage(previewImage)}
                    style={{
                      cursor: "pointer",
                      border: selectedImage === previewImage
                        ? "3px solid #2ecc71"
                        : "2px solid transparent",
                      borderRadius: "12px",
                      padding: "4px"
                    }}
                  >
                    <p>Dopo</p>
                    <img src={previewImage} alt="Dopo" />
                  </div>
                </div>

                <div className="ai-presets">
                  {QUICK_PRESETS.map((p) => (
                    <button
                      key={p}
                      type="button"
                      className="ai-preset-btn"
                      disabled={loading}
                      onClick={() => setEditPrompt(p)}
                    >
                      {p}
                    </button>
                  ))}
                </div>

                <textarea
                  className="ai-edit-input"
                  placeholder="Es: metti il panino su un piatto giallo..."
                  value={editPrompt}
                  onChange={(e) => setEditPrompt(e.target.value)}
                  disabled={loading}
                />

                <div className="ai-actions">
                  <button
                    type="button"
                    className="ai-btn-edit"
                    disabled={loading || !editPrompt.trim()}
                    onClick={handleEditImage}
                  >
                    {loading ? "⏳ Modifica in corso..." : "✏️ Modifica con AI"}
                  </button>

                  <button
                    type="button"
                    className="ai-btn-confirm"
                    onClick={handleUse}
                    disabled={!selectedImage}
                    style={{
                      opacity: !selectedImage ? 0.5 : 1,
                      cursor: !selectedImage ? "not-allowed" : "pointer"
                    }}
                  >
                    ✅ Conferma immagine
                  </button>
                </div>
              </div>
            )}

            {showSinglePreview && (
              <div className="ai-preview">

  <p className="ai-sub">
    Immagine selezionata
  </p>
                <img src={selectedImage} alt="Anteprima piatto" />

                <div className="ai-presets">
                  {QUICK_PRESETS.map((p) => (
                    <button
                      key={p}
                      type="button"
                      className="ai-preset-btn"
                      disabled={loading}
                      onClick={() => setEditPrompt(p)}
                    >
                      {p}
                    </button>
                  ))}
                </div>

                <textarea
                  className="ai-edit-input"
                  placeholder="Es: metti il panino su un piatto giallo..."
                  value={editPrompt}
                  onChange={(e) => setEditPrompt(e.target.value)}
                  disabled={loading}
                />

                <div className="ai-actions">
                  <button
                    type="button"
                    className="ai-btn-edit"
                    disabled={loading || !editPrompt.trim()}
                    onClick={handleEditImage}
                  >
                    {loading ? "⏳ Modifica in corso..." : "✏️ Modifica con AI"}
                  </button>

                  <button
                    type="button"
                    className="ai-btn-confirm"
                    disabled={loading}
                    onClick={handleUse}
                  >
                    ✅ Conferma immagine
                  </button>
                </div>
              </div>
            )}

            {!loading &&
              !showGeneratedChoices &&
              !showSinglePreview &&
              !showCompare &&
              !showLoadingCompare && (
                <p className="ai-empty">
  Seleziona uno stile e genera un'immagine AI del piatto
</p>
              )}
          </div>
        </div>

        <div className="ai-footer">

  <button
    type="button"
    className="ai-cancel"
    onClick={handleClose}
    disabled={loading}
  >
    Chiudi
  </button>

</div>
      </div>
    </div>
  );
}